<template>
    <b-modal
        v-if="category"
        :id="'portfolio-edit-add-assignment-modal-' + category.id"
        :ref="'portfolio-edit-add-assignment-modal-' + category.id"
        :title="$t('portfolio.add_exercise')"
        :ok-disabled="name === null && choice === 1"
        :ok-title="choice == null ? $t('common.close') : $t('common.save')"
        :cancel-title="$t('common.cancel')"
        cancel-variant="outline-primary"
        :ok-only="choice === null"
        @ok="onOk"
        @show="onShow"
        :hide-footer="choice !== 1"
        ok-variant="primary"
        modal-class="apprentice-default-modal portfolio-edit-add-assignment-modal"
        size="xl">
        <b-alert
            v-if="hasError"
            variant="danger"
            show>
            {{ errorMessage }}
        </b-alert>

        <div
            class="assignment-choices"
            v-if="choice === null">
            <div class="row">
                <div class="col-12 col-md-6 col-lg-4 new-assignment">
                    <div class="add-new-assignment apprentice-simple-card">
                        <div class="new-assignment-icon icon">
                            <i class="btn btn-primary-icon-only btn-add-layer"></i>
                        </div>
                        <div class="new-assignment-content content">
                            <h5 class="title">{{ $t('portfolio.new_assignment') }}</h5>
                            <div class="description">
                                {{
                                    $t('portfolio.start_with_an_empty_exercise_as_a_basis_for_creating_a_new_exercise')
                                }}
                            </div>
                        </div>
                        <a
                            class="clickable"
                            href=""
                            @click.prevent.stop="choice = 1"></a>
                    </div>
                </div>

                <div class="col-12 col-md-6 col-lg-4 existing-assignment">
                    <div class="new-existing-assignment apprentice-simple-card">
                        <div class="new-existing-assignment-icon icon">
                            <i class="btn btn-primary-icon-only btn-books"></i>
                        </div>
                        <div class="new-assignment-content content">
                            <h5 class="title">{{ $t('portfolio.own_exercises') }}</h5>
                            <div class="description">
                                {{
                                    $t(
                                        'portfolio.choose_a_previously_completed_assignment_within_your_school_as_a_starting_point_for_a_new_assignment',
                                    )
                                }}
                            </div>
                        </div>
                        <a
                            class="clickable"
                            href=""
                            @click.prevent.stop="showAddExistingAssignmentModal"></a>
                    </div>
                </div>

                <div
                    class="col-12 col-md-6 col-lg-4 shared-assignment"
                    v-if="!$authUser.isStudent()">
                    <div class="new-shared-assignment apprentice-simple-card">
                        <div class="new-shared-assignment-icon icon">
                            <i class="btn btn-primary-icon-only btn-clipboard-user"></i>
                        </div>
                        <div class="new-assignment-content content">
                            <h5 class="title">{{ $t('portfolio.assignment_from_database') }}</h5>
                            <div class="description">
                                {{
                                    $t(
                                        'portfolio.choose_from_all_the_assignments_in_our_database_and_use_this_as_a_basis_for_your_new_assignment',
                                    )
                                }}
                            </div>
                        </div>
                        <a
                            class="clickable"
                            href=""
                            @click.prevent.stop="showAddAssignmentSharedModal"></a>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="choice === 1">
            <div v-if="portfolioIn.worldmap">
                <design-2021-worldmap-component
                    @country-tabbed="setCountry($event)"
                    :use-in="'country-picker'"
                    :countries-in="countries"
                    :selected-continent-in="category.continent"
                    @setup-continent="setupContinent"
                    ref="worldmap-component"></design-2021-worldmap-component>
                <br />
            </div>

            <div class="assignment-new-name">
                <b-form-group
                    class="assignment-title-group"
                    :label="$t('portfolio.title_exercise')"
                    label-for="portfolio-edit-title-input">
                    <b-form-input
                        id="portfolio-edit-title-input"
                        v-model="name"
                        :placeholder="$t('portfolio.type_the_name_of_the_assignment_here')">
                    </b-form-input>
                </b-form-group>

                <b-form-group
                    class="available-tags-group"
                    :label="$t('portfolio.choose_tag')"
                    label-for="assignment-available-tags-select">
                    <design-2021-apprentice-multiselect-component
                        class="available-tags"
                        v-model="selectedTag"
                        :placeholder="
                            availableTags.length !== 0 ? $t('portfolio.choose_tag') : $t('portfolio.no_tags_found')
                        "
                        label="name"
                        track-by="id"
                        :allow-empty="false"
                        :multiple-select="false"
                        :options="availableTags"
                        :show-labels="false"
                        :disabled="availableTags.length === 0">
                        <template #singleLabel="props">
                            <div class="tag-icon">
                                <i
                                    class="fas fa-fw fa-circle"
                                    :style="{ color: props.option.color }"></i>
                                {{ props.option.name }}
                            </div>
                        </template>

                        <template #option="props">
                            <div class="tag-option">
                                <i
                                    class="fas fa-fw fa-circle"
                                    :style="{ color: props.option.color }"></i
                                >{{ props.option.name }}
                            </div>
                        </template>
                    </design-2021-apprentice-multiselect-component>
                </b-form-group>
            </div>
        </div>
    </b-modal>
</template>

<script>
import Design2021WorldmapComponent from '../../../worldmap/design-2021-worldmap-component.vue';
import { worldmapData } from '../../../../helpers/worldmapData';

const defaultSelectionColor = '#07A1FF';

export default {
    name: 'design-2021-portfolio-edit-add-new-assignment-modal-component',
    components: { Design2021WorldmapComponent },
    props: ['category', 'portfolioIn', 'courseId', 'educationLevels', 'availableTags', 'groupId'],
    data() {
        return {
            name: null,
            portfolio: this.portfolioIn,
            hasError: false,
            choice: null,
            errorMessage: '',
            selectedTag: null,
            country: null,
            color: defaultSelectionColor,
        };
    },
    computed: {
        countries() {
            let countries = {};

            Object.keys(worldmapData.countries).forEach((countryCode) => {
                countries[countryCode] = {
                    country: countryCode,
                    countryName: worldmapData.countries[countryCode].country,
                    state: 'none',
                };
            });

            this.category.assignments.map((assignment) => {
                if (assignment.country) {
                    countries[assignment.country].country = assignment.country;
                    countries[assignment.country].assignmentName = assignment.name;
                    countries[assignment.country].statusColor = null;
                    countries[assignment.country].state =
                        assignment.id === this.assignmentIn?.id
                            ? 'selected'
                            : assignment.id !== null
                              ? 'blocked'
                              : 'none';
                }
            });

            return countries;
        },
    },
    methods: {
        onOk(bvModalEvt) {
            if (this.choice === 1) {
                this.hasError = false;

                if (this.name === null || this.name === '') {
                    this.hasError = true;
                    this.errorMessage = this.$t('portfolio.assignment_name_mandatory');
                }

                if (this.name && this.name.length > 192) {
                    this.hasError = true;
                    this.errorMessage = this.$t('portfolio.assignment_name_cannot_contain_more_than_191_characters');
                }

                const values = {
                    name: this.name,
                    category_id: this.category.id,
                    copy: this.existingOption,
                    assignment_id: this.selectedAssignment,
                    country: this.country,
                    color: this.color,
                };

                if (this.hasError) {
                    bvModalEvt.preventDefault();
                } else {
                    this.createAssignment(values);
                }
            }
        },

        createAssignment(values) {
            this.alert.saving(
                this.$t('portfolio.add_exercise'),
                this.$t('portfolio.exercise_will_be_added_please_wait_a_moment'),
            );

            let order = 0;
            for (const category of this.portfolio.categories) {
                if (category.id === values.category_id) {
                    order = category.assignments.length + 1;
                    break;
                }
            }

            let data = {
                name: values.name,
                category_id: values.category_id,
                order: order,
                assignment_id: values.assignment_id,
                country: this.country,
            };

            if (this.selectedTag) {
                data.tag_id = this.selectedTag.id;
                data.tag_name = this.selectedTag.name;
                data.tag_color = this.selectedTag.color;
            }

            const route =
                values.copy === '1' ? this.routes.portfolioCopyAssignment() : this.routes.portfolioCategoryAssignment();

            this.axios.post(route, data).then(
                (response) => {
                    this.portfolio.categories = this.portfolio.categories.map((category) => {
                        if (category.id !== values.category_id) {
                            return category;
                        }
                        category.assignments.push(response.data);
                        return category;
                    });

                    this.alert.success();

                    window.location.href = this.routes.assignmentEdit(
                        response.data.id,
                        this.groupId,
                    );
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },

        onShow() {
            this.choice = null;
            this.name = null;
            this.selectedAssignment = null;
            this.selectedTag = null;
            this.country = null;
            this.color = defaultSelectionColor;
            this.hasError = false;
        },

        showAddExistingAssignmentModal() {
            this.$bvModal.show(`portfolio-edit-add-existing-assignment-modal-${this.category.id}`);
            this.$bvModal.hide(`portfolio-edit-add-assignment-modal-${this.category.id}`);
        },

        showAddAssignmentSharedModal() {
            this.$bvModal.show(`assignment-edit-add-shared-assignment-modal`);
            this.$bvModal.hide('portfolio-edit-add-assignment-modal-' + this.category.id);
        },
        setCountry(country) {
            this.country = country !== this.country ? country : null;
            this.$refs['worldmap-component'].changeSelectedCountry(this.country);
        },
        revertChanges() {
            this.$refs['worldmap-component']?.changeSelectedCountry(null); // revert map selection
        },

        setupContinent(_) {
            this.$emit('setup-continent');
        },
    },
    mounted() {
        this.$root.$on('bv::modal::hide', (bvEvent, modalID) => {
            if (bvEvent.trigger !== 'ok' && modalID === 'portfolio-edit-add-assignment-modal-' + this.category?.id) {
                this.revertChanges();
            }
        });
    },
};
</script>

<style scoped lang="scss">
.new-assignment,
.existing-assignment,
.shared-assignment {
    margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');
}

.add-new-assignment,
.new-existing-assignment,
.new-shared-assignment {
    height: 100%;
    position: relative;
    background-color: $secondary-background-color;

    .icon {
        margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');

        i {
            font-size: 6rem;
        }
    }

    .content {
        .title {
            margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
        }

        .description {
            color: $text-muted-color;
        }
    }

    &:hover {
        cursor: pointer;
        box-shadow: 1px 0px 4px $border-color;

        .icon {
            i {
                color: var(--primaryLightest);
            }
        }
    }
}

.assignment-new-name {
    display: flex;
    justify-content: space-between;
    row-gap: $grid-gutter-width;
    align-items: flex-start;

    input {
        border: 1px solid $border-color;
    }

    .assignment-title-group {
        flex-basis: calc(67% - 15px);
    }

    .available-tags-group {
        flex-basis: calc(33% - 15px);
    }
}

@media (min-width: map-get($grid-breakpoints, 'lg')) {
    .new-assignment,
    .existing-assignment,
    .shared-assignment {
        margin-bottom: 0;
    }
}
</style>
